import { z } from 'zod';
import {
  DocumentType,
  EducationLevel,
  Gender,
  MaritalStatus,
  Proficiency,
  ShiftType,
  StudyMode,
} from '../enums';
import { AADHAAR_REGEX, IFSC_REGEX, PAN_REGEX, UAN_REGEX } from '../constants';
import { addressSchema, booleanish, dateSchema, emailSchema, phoneSchema, zEnum } from './common';

const MIN_AGE = 14;
const MAX_AGE = 65;

export const seekerProfileSchema = z.object({
  fullName: z.string().trim().min(2, 'Enter your full name').max(120),
  fatherName: z.string().trim().max(120).optional().or(z.literal('')),
  dateOfBirth: dateSchema.refine(
    (d) => {
      const age = (Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
      return age >= MIN_AGE && age <= MAX_AGE;
    },
    { message: `Age must be between ${MIN_AGE} and ${MAX_AGE}` },
  ),
  gender: zEnum(Gender),
  maritalStatus: zEnum(MaritalStatus).optional(),
  email: emailSchema.optional().or(z.literal('')),
  alternatePhone: phoneSchema.optional(),
  address: addressSchema,
  /** Kept separate from `address` — workers often live away from their native place. */
  nativeState: z.string().trim().max(100).optional().or(z.literal('')),
  nativeDistrict: z.string().trim().max(100).optional().or(z.literal('')),
  languagesKnown: z.array(z.string().trim().max(40)).max(10).default([]),
  headline: z.string().trim().max(150).optional().or(z.literal('')),
  about: z.string().trim().max(2000).optional().or(z.literal('')),
  hasTwoWheeler: z.boolean().default(false),
  hasDrivingLicence: z.boolean().default(false),
  isDifferentlyAbled: z.boolean().default(false),
});
export type SeekerProfileInput = z.infer<typeof seekerProfileSchema>;

/**
 * One row per qualification held, from 10th upward. Exactly one row should be
 * flagged `isHighest`; the API recomputes `highestQualification` and its rank
 * on the profile whenever this list changes.
 */
export const educationSchema = z
  .object({
    level: zEnum(EducationLevel),
    /** e.g. "Fitter" for ITI, "Mechanical Engineering" for Diploma/BE. */
    courseOrTrade: z.string().trim().max(150).optional().or(z.literal('')),
    specialization: z.string().trim().max(150).optional().or(z.literal('')),
    institution: z.string().trim().min(2, 'Enter the school / college name').max(200),
    boardOrUniversity: z.string().trim().max(200).optional().or(z.literal('')),
    mode: zEnum(StudyMode).default(StudyMode.REGULAR),
    yearOfPassing: z.coerce
      .number()
      .int()
      .min(1960)
      .max(new Date().getFullYear() + 5),
    /** Percentage; convert CGPA before sending. */
    percentage: z.coerce.number().min(0).max(100).optional(),
    isPursuing: z.boolean().default(false),
    isHighest: z.boolean().default(false),
  })
  .refine((v) => ![EducationLevel.ITI, EducationLevel.DIPLOMA].includes(v.level as never) || !!v.courseOrTrade, {
    message: 'Trade / branch is required for ITI and Diploma',
    path: ['courseOrTrade'],
  });
export type EducationInput = z.infer<typeof educationSchema>;

export const workExperienceSchema = z
  .object({
    companyName: z.string().trim().min(2, 'Enter the company name').max(200),
    designation: z.string().trim().min(2, 'Enter your designation').max(120),
    department: z.string().trim().max(120).optional().or(z.literal('')),
    /** Set when the previous role was via a contractor — common in this sector. */
    employerType: z.enum(['DIRECT', 'CONTRACT', 'APPRENTICE', 'TRAINEE']).default('DIRECT'),
    location: z.string().trim().max(150).optional().or(z.literal('')),
    fromDate: dateSchema,
    toDate: dateSchema.optional(),
    isCurrent: z.boolean().default(false),
    lastDrawnSalary: z.coerce.number().min(0).max(10_000_000).optional(),
    reasonForLeaving: z.string().trim().max(300).optional().or(z.literal('')),
    responsibilities: z.string().trim().max(2000).optional().or(z.literal('')),
    machinesHandled: z.array(z.string().trim().max(80)).max(20).default([]),
  })
  .refine((v) => v.isCurrent || !!v.toDate, {
    message: 'Enter the end date, or mark this as your current job',
    path: ['toDate'],
  })
  .refine((v) => !v.toDate || v.toDate >= v.fromDate, {
    message: 'End date cannot be before the start date',
    path: ['toDate'],
  });
export type WorkExperienceInput = z.infer<typeof workExperienceSchema>;

export const seekerSkillSchema = z.object({
  skillId: z.string().uuid(),
  proficiency: zEnum(Proficiency).default(Proficiency.INTERMEDIATE),
  yearsOfExperience: z.coerce.number().min(0).max(50).optional(),
});
export const setSeekerSkillsSchema = z.object({
  skills: z.array(seekerSkillSchema).max(50),
});
export type SetSeekerSkillsInput = z.infer<typeof setSeekerSkillsSchema>;

export const jobPreferenceSchema = z.object({
  preferredShift: zEnum(ShiftType).default(ShiftType.ANY),
  expectedSalaryMin: z.coerce.number().min(0).max(10_000_000).optional(),
  expectedSalaryMax: z.coerce.number().min(0).max(10_000_000).optional(),
  currentSalary: z.coerce.number().min(0).max(10_000_000).optional(),
  noticePeriodDays: z.coerce.number().int().min(0).max(180).default(0),
  willingToRelocate: z.boolean().default(true),
  needsAccommodation: z.boolean().default(false),
  availableFrom: dateSchema.optional(),
  preferredCategoryIds: z.array(z.string().uuid()).max(10).default([]),
  preferredLocations: z
    .array(
      z.object({
        state: z.string().trim().min(2).max(100),
        district: z.string().trim().max(100).optional().or(z.literal('')),
        city: z.string().trim().max(100).optional().or(z.literal('')),
      }),
    )
    .max(10)
    .default([]),
  /** Suppresses notifications without deleting the profile. */
  isOpenToWork: z.boolean().default(true),
});
export type JobPreferenceInput = z.infer<typeof jobPreferenceSchema>;

/**
 * Sensitive identifiers. The raw value is encrypted at rest and never returned
 * by the API — reads get `aadhaarLast4` / `panMasked` instead.
 */
export const kycSchema = z.object({
  aadhaarNumber: z.string().regex(AADHAAR_REGEX, 'Aadhaar must be 12 digits').optional(),
  panNumber: z
    .string()
    .trim()
    .toUpperCase()
    .regex(PAN_REGEX, 'Enter a valid PAN (e.g. ABCDE1234F)')
    .optional(),
  uanNumber: z.string().regex(UAN_REGEX, 'UAN must be 12 digits').optional(),
  esicNumber: z.string().trim().max(20).optional(),
  bankAccountNumber: z.string().trim().regex(/^\d{9,18}$/, 'Enter a valid account number').optional(),
  bankIfsc: z.string().trim().toUpperCase().regex(IFSC_REGEX, 'Enter a valid IFSC code').optional(),
  bankName: z.string().trim().max(120).optional(),
  /** Explicit consent is required before we may store Aadhaar (DPDP Act 2023). */
  consentToStoreKyc: z.boolean().default(false),
});
export type KycInput = z.infer<typeof kycSchema>;

export const presignUploadSchema = z.object({
  type: zEnum(DocumentType),
  fileName: z.string().trim().min(1).max(255),
  mimeType: z.string().trim().min(3).max(120),
  sizeBytes: z.coerce.number().int().min(1),
});
export type PresignUploadInput = z.infer<typeof presignUploadSchema>;

export const confirmUploadSchema = z.object({
  type: zEnum(DocumentType),
  fileKey: z.string().trim().min(3).max(500),
  fileName: z.string().trim().min(1).max(255),
  mimeType: z.string().trim().min(3).max(120),
  sizeBytes: z.coerce.number().int().min(1),
  /** Certificates and ID cards that lapse — drives expiry reminders. */
  expiresAt: dateSchema.optional(),
  documentNumber: z.string().trim().max(64).optional(),
});
export type ConfirmUploadInput = z.infer<typeof confirmUploadSchema>;

export const seekerSearchSchema = z.object({
  q: z.string().trim().max(120).optional(),
  skillIds: z.array(z.string().uuid()).optional(),
  minQualification: zEnum(EducationLevel).optional(),
  qualifications: z.array(zEnum(EducationLevel)).optional(),
  /** Matches Education.courseOrTrade — e.g. "Fitter", "Mechanical Engineering". */
  trade: z.string().trim().max(120).optional(),
  gender: zEnum(Gender).optional(),
  minExperienceMonths: z.coerce.number().int().min(0).optional(),
  maxExperienceMonths: z.coerce.number().int().min(0).optional(),
  minAge: z.coerce.number().int().min(MIN_AGE).optional(),
  maxAge: z.coerce.number().int().max(MAX_AGE).optional(),
  state: z.string().trim().max(100).optional(),
  district: z.string().trim().max(100).optional(),
  maxExpectedSalary: z.coerce.number().min(0).optional(),
  preferredShift: zEnum(ShiftType).optional(),
  willingToRelocate: booleanish.optional(),
  isVerified: booleanish.optional(),
  isOpenToWork: booleanish.optional(),
  /** Opt-in narrowing for a recruiter who wants a CV in hand; absent means "no opinion". */
  hasResume: booleanish.optional(),
  /** Exclude candidates already in the pipeline for this job. */
  excludeAppliedToJobId: z.string().uuid().optional(),
});
export type SeekerSearchQuery = z.infer<typeof seekerSearchSchema>;
